import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const ChatbotsForNonprofits = () => {
  const navigate = useNavigate();
  return (
    <>
      <SEO 
        title="The Ultimate Guide to Chatbots for Nonprofits | CauseConnect" 
        description="Learn how chatbots can transform your nonprofit's engagement, streamline operations, and maximize impact with CauseConnect's comprehensive guide."
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-[#2D7D46] text-white py-20">
          <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-10" />
          <div className="container mx-auto px-4 relative">
            <div className="max-w-4xl mx-auto">
              <Link to="/guides" className="inline-flex items-center text-white/80 hover:text-white mb-8">
                ← Back to Guides
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                The Ultimate Guide to Chatbots for Nonprofits
              </h1>
              <p className="text-xl text-gray-100">
                Top Tools, Benefits, and How CauseConnect Can Help
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="lead">
              In today's digital-first world, nonprofits must leverage technology to engage supporters, 
              streamline communications, and ultimately further their missions. One tool that stands out 
              in helping nonprofits achieve these goals is the chatbot.
            </p>

            <h2>What Is a Chatbot and Why Is It Important for Nonprofits?</h2>
            <p>
              A chatbot is a software application designed to simulate conversations with human users, 
              usually over the internet via a website, social media platform, or messaging app. Modern 
              chatbots are powered by AI (Artificial Intelligence) or rule-based systems, allowing them 
              to respond to user queries in real time and deliver quick, efficient support.
            </p>

            <h3>Why Is This Important for Nonprofits?</h3>
            <ul>
              <li>
                <strong>24/7 Availability:</strong> Nonprofits often operate with limited staff and resources. 
                A chatbot can provide round-the-clock support, answering common questions, processing donations, 
                and guiding users to relevant resources even when your team is offline.
              </li>
              <li>
                <strong>Cost-Effectiveness:</strong> Hiring staff or volunteers to respond to inquiries 24/7 
                can be expensive. A chatbot reduces operational costs while still offering high-quality support.
              </li>
              <li>
                <strong>Enhanced Engagement:</strong> Immediate, helpful responses keep donors and volunteers 
                engaged. Chatbots can also deliver personalized messages that improve user experience.
              </li>
              <li>
                <strong>Efficient Data Collection:</strong> You can collect valuable data on user preferences, 
                donor interests, and frequently asked questions. This information helps you tailor campaigns 
                and improve donor relationships.
              </li>
            </ul>


            <h2>Key Benefits of Implementing a Well-Set-Up Chatbot</h2>
            <h3>Improved Donor Experience</h3>
            <p>
              Donors are more likely to give (and give again) when they have a quick, frictionless experience. 
              A chatbot can facilitate instant donation processes and provide relevant information about how 
              funds are used.
            </p>

            <h3>Optimized Volunteer Management</h3>
            <p>
              Volunteers often need guidance on upcoming events, schedules, and tasks. A chatbot can automate 
              sign-ups, share event details, and answer general queries.
            </p>

            <h3>Streamlined Communication</h3>
            <p>
              Sending out event reminders, updates on projects, and success stories is made easier with 
              automated chat sequences.
            </p>

            <h2>Top Five Chatbot Tools for Nonprofits</h2>
            <div className="not-prose">
              <div className="grid gap-6 md:grid-cols-2 my-8">
                {[
                  {
                    name: "Chatfuel",
                    description: "Focus on Facebook Messenger bots with drag-and-drop modules and templates.",
                    pricing: "Free plan available, paid plans from $15/month"
                  },
                  {
                    name: "ManyChat",
                    description: "Popular for marketing automation across multiple platforms.",
                    pricing: "Free plan available, pro plans from $15/month"
                  },
                  {
                    name: "MobileMonkey",
                    description: "Offers multi-platform chatbots with unified inbox.",
                    pricing: "Basic free plan, paid from $21/month"
                  },
                  {
                    name: "Tidio",
                    description: "Live chat and chatbot combo with intuitive builder.",
                    pricing: "Limited free plan, paid from $19/month"
                  },
                  {
                    name: "Intercom",
                    description: "Premium tool with advanced targeting and personalization.",
                    pricing: "Paid plans from $59/month"
                  }
                ].map((tool) => (
                  <div key={tool.name} className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="text-xl font-semibold mb-2">{tool.name}</h4>
                    <p className="text-gray-600 mb-2">{tool.description}</p>
                    <p className="text-sm text-gray-500">{tool.pricing}</p>
                  </div>
                ))}
              </div>
            </div>

            <h2>How CauseConnect's Free AI Assistant Fits In</h2>
            <p>
              Among the many chatbot tools available, CauseConnect stands out by offering a free chatbot 
              solution specifically tailored to the unique needs of nonprofits. While the tools listed 
              above are excellent, they often have subscription fees that can be challenging for 
              organizations on tight budgets.
            </p>

            <div className="bg-green-50 p-8 rounded-lg my-8">
              <h3 className="text-green-800">Ready to Transform Your Nonprofit's Digital Engagement?</h3>
              <p className="text-green-700 mb-4">
                Get started with CauseConnect's free nonprofit chatbot and take your first step toward 
                enhanced donor and volunteer engagement.
              </p>
              <div className="mt-6">
                <Button 
                  className="bg-[#2E7D32] hover:bg-[#2E7D32]/90 text-white"
                  onClick={() => navigate('/signup')}
                >
                  Get Started Free
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatbotsForNonprofits;
